import React from "react";
import LabelValuePair from "../common/LabelValuePair";
import { AuditLog, AuditLogTypeLabels } from "../../models/AuditLog";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

import { dialogWide } from "@/lib/dialogSize";

const STATUS: Record<string, { label: string; variant: "success" | "danger" | "neutral" | "secondary" }> = {
  Pending: { label: "Submitted, not run yet", variant: "neutral" },
  Succeeded: { label: "Success", variant: "success" },
  Failed: { label: "Failed", variant: "danger" },
  Cancelled: { label: "Cancelled before it ran", variant: "secondary" },
};

export const ViewSqlScriptExecuteAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({ auditLog, open, onClose, organizationId }) => {
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`${dialogWide} overflow-hidden`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {AuditLogTypeLabels[auditLog.eventType]}
              </DialogTitle>
            </DialogHeader>

            <Separator />

            <div className="flex flex-col gap-2 overflow-y-auto py-2">
              {auditLog.additionalData && (() => {
                try {
                  const additionalDataAsJson = JSON.parse(auditLog.additionalData);
                  // Entries written only once the script ran carry no State, just Success.
                  const state: string = additionalDataAsJson.State
                    ?? (additionalDataAsJson.Success ? "Succeeded" : "Failed");
                  const status = STATUS[state] ?? STATUS.Failed;

                  return (
                    <>
                      <div className="flex flex-row gap-2 items-center">
                        <span className="text-sm font-bold">Status:</span>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </div>

                      <LabelValuePair
                        label="Reason"
                        value={additionalDataAsJson.Reason}
                      />

                      {state === "Succeeded" && (
                        <>
                          <LabelValuePair
                            label="Affected Rows"
                            value={additionalDataAsJson.AffectedRows ?? "N/A"}
                          />
                          <LabelValuePair
                            label="Duration"
                            value={additionalDataAsJson.DurationMs != null ? `${(additionalDataAsJson.DurationMs / 1000).toFixed(2)}s` : "N/A"}
                          />
                        </>
                      )}

                      {state === "Failed" && additionalDataAsJson.Error && (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-bold">Error:</span>
                          <div className="rounded-sm bg-destructive/10 p-3 max-h-[150px] overflow-y-auto">
                            <pre className="m-0 font-mono text-sm whitespace-pre-wrap break-all">
                              {additionalDataAsJson.Error}
                            </pre>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-bold">
                          SQL Script:
                        </span>
                        <div className="rounded-sm bg-muted p-3 max-h-[300px] overflow-y-auto">
                          <pre className="m-0 font-mono text-sm whitespace-pre-wrap break-all">
                            {additionalDataAsJson.SqlScript}
                          </pre>
                        </div>
                      </div>
                    </>
                  );
                } catch (error) {
                  return (
                    <span className="text-sm">
                      Failed to parse additionalData
                    </span>
                  );
                }
              })()}

              <LabelValuePair
                label="Organization ID"
                value={organizationId}
              />

              <LabelValuePair
                label="Performed By"
                value={auditLog.performerEmailAddress || auditLog.performedBy}
              />

              <LabelValuePair
                label="Event Date"
                value={auditLog.eventDate}
              />
            </div>
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};
